import React, {
	createContext,
	useCallback,
	useContext,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { createPortal } from "react-dom";
import { tooltipPosition, type TooltipPlacement } from "./tooltip_position";

// The app's tooltips. The browser's own `title` was doing this job and doing it
// late — about a second of hover before anything appears, which is a second of
// nothing in front of a room. These show the moment the pointer arrives.
//
// One bubble for the whole app, in a portal: a tooltip drawn in place would be
// clipped by whatever scroller it sits in and would have to win a stacking
// context it does not own. Controls opt in through a hook that returns handlers
// to spread, so no call site grows a wrapper element — with sixty-odd of them
// across headers, toolbars and popovers, an extra node each would have quietly
// changed as many layouts. Where the control is rendered by someone else's
// component and there are no props to spread, wrap it instead (../rail_tooltip
// is that shape, and is the rail's alone).

const BUBBLE_CLASS = [
	// No nowrap: most labels are two words and sit on one line anyway, but a few
	// carry a whole verse (an assistant citation), and one line of that would run
	// off the screen.
	// Above every layer the app draws: the top header sits at 1000, modals and the
	// sidebar in the 10000s, and admin dropdowns at 1000000. A tooltip belongs on
	// top of whatever it labels, and the portal puts it in the root stacking
	// context, so one number settles it everywhere.
	"pointer-events-none fixed z-[2000000] max-w-[280px] rounded-[6px] px-[8px] pt-[4px] pb-[3px]",
	// The app's own tokens, and the same pair the rail's tooltip uses, so one
	// look everywhere. They live outside any theme wrapper, which is what the
	// reader-specific variables could not do from a portal on <body>.
	"bg-dark-base text-[11px] leading-[1.45] font-medium text-white dark:bg-white dark:text-dark-base",
	"shadow-[0_4px_12px_rgba(26,26,46,0.24)]",
].join(" ");

interface Anchor {
	left: number;
	top: number;
	width: number;
	height: number;
}

interface TooltipTarget {
	label: string;
	anchor: Anchor;
}

/** What a control spreads onto itself. Deliberately only pointer/focus
 *  handlers: no ref, no wrapper, nothing that changes the element. */
export interface TooltipHandlers {
	onPointerEnter: (e: React.PointerEvent<HTMLElement>) => void;
	onPointerLeave: (e: React.PointerEvent<HTMLElement>) => void;
	// The event is passed on, not swallowed: the draw toolbar's size buttons
	// suppress the default pointerdown to keep the label being typed in focus.
	onPointerDown: (e: React.PointerEvent<HTMLElement>) => void;
	onFocus: (e: React.FocusEvent<HTMLElement>) => void;
	onBlur: (e: React.FocusEvent<HTMLElement>) => void;
}

/** A control's own handlers, folded in rather than overwritten: several of
 *  these controls already listen for the same events (the column grip starts a
 *  drag on pointerdown, a citation previews itself on focus), and a spread that
 *  replaced them would break them silently. */
type OwnHandlers = Partial<TooltipHandlers>;

type TooltipApi = (label: string | undefined, own?: OwnHandlers) => TooltipHandlers;

const NO_TOOLTIP: TooltipApi = (_label, own) => ({
	onPointerEnter: e => own?.onPointerEnter?.(e),
	onPointerLeave: e => own?.onPointerLeave?.(e),
	onPointerDown: e => own?.onPointerDown?.(e),
	onFocus: e => own?.onFocus?.(e),
	onBlur: e => own?.onBlur?.(e),
});

const TooltipContext = createContext<TooltipApi | null>(null);

/** Handlers to spread onto a control that wants a label on hover. One call per
 *  component, then `{...tip("Undo")}` per control — a factory rather than a
 *  hook per element, since half of these controls are rendered in a map. */
export const useTooltip = (): TooltipApi => useContext(TooltipContext) ?? NO_TOOLTIP;

export const TooltipProvider = ({ children }: { children: React.ReactNode }) => {
	const [target, setTarget] = useState<TooltipTarget | null>(null);
	const bubbleRef = useRef<HTMLDivElement>(null);
	// Measured after the bubble exists, so its own width is known before it is
	// placed. Until then it is rendered invisible rather than at a guessed spot,
	// which would show as a jump.
	const [at, setAt] = useState<TooltipPlacement | null>(null);
	const anchorElRef = useRef<HTMLElement | null>(null);

	const hide = useCallback(() => {
		anchorElRef.current = null;
		setTarget(null);
		setAt(null);
	}, []);

	const show = useCallback((label: string, el: HTMLElement) => {
		const { left, top, width, height } = el.getBoundingClientRect();
		anchorElRef.current = el;
		setAt(null);
		setTarget({ label, anchor: { left, top, width, height } });
	}, []);

	useLayoutEffect(() => {
		const bubble = bubbleRef.current;
		if (!target || !bubble) return;
		setAt(
			tooltipPosition(
				target.anchor,
				{ width: bubble.offsetWidth, height: bubble.offsetHeight },
				{ width: window.innerWidth, height: window.innerHeight },
			),
		);
	}, [target]);

	// The anchor's rect was measured once; scrolling or resizing makes it a lie,
	// and a bubble left behind pointing at nothing is worse than no bubble.
	useLayoutEffect(() => {
		if (!target) return;
		window.addEventListener("scroll", hide, true);
		window.addEventListener("resize", hide);
		return () => {
			window.removeEventListener("scroll", hide, true);
			window.removeEventListener("resize", hide);
		};
	}, [target, hide]);

	// A control removed under the pointer never gets pointerleave — a menu that closes
	// itself (on typing, on Escape) took its buttons with it and left the label behind.
	useLayoutEffect(() => {
		if (!target) return;
		const observer = new MutationObserver(() => {
			if (!anchorElRef.current?.isConnected) hide();
		});
		observer.observe(document.body, { childList: true, subtree: true });
		return () => observer.disconnect();
	}, [target, hide]);

	const api = useMemo<TooltipApi>(
		() => (label, own) => ({
			// Touch has no hover: there the bubble would appear on tap, over the
			// thing just tapped, and linger.
			onPointerEnter: e => {
				own?.onPointerEnter?.(e);
				if (e.pointerType === "touch" || !label) return;
				show(label, e.currentTarget);
			},
			onPointerLeave: e => {
				own?.onPointerLeave?.(e);
				hide();
			},
			// The reader has read it and is now using the control; keeping the
			// bubble up only hides what the click did.
			onPointerDown: e => {
				own?.onPointerDown?.(e);
				hide();
			},
			// Keyboard focus deserves the same label, but a click also focuses —
			// and there the bubble was just dismissed on pointerdown, so it must
			// not come straight back. :focus-visible is exactly that distinction.
			onFocus: e => {
				own?.onFocus?.(e);
				if (!label) return;
				try {
					if (!e.currentTarget.matches(":focus-visible")) return;
				} catch {
					// Older engines (and jsdom) may not know the selector; a tooltip
					// on focus is a smaller problem than a crash.
				}
				show(label, e.currentTarget);
			},
			onBlur: e => {
				own?.onBlur?.(e);
				hide();
			},
		}),
		[show, hide],
	);

	return (
		<TooltipContext.Provider value={api}>
			{children}
			{target &&
				createPortal(
					<div
						ref={bubbleRef}
						role="tooltip"
						className={BUBBLE_CLASS}
						style={{
							...(at
								? { left: at.left, top: at.top }
								: // Invisible for the one frame it takes to measure.
									{ left: 0, top: 0, visibility: "hidden" }),
						}}
					>
						{target.label}
					</div>,
					document.body,
				)}
		</TooltipContext.Provider>
	);
};
